import { RootStore } from 'store/RootStore'
import { GENERAL_STORE } from 'store/GeneralStore'
import { Api, parseToCatalog, formatDuration, getStartAndEndDates } from 'utilities'
import {
  Viewer, ViewerInterface, ViewerStatistics,
  ViewerOrderStatistics, OrderInterface, ViewerEventEnums,
  ViewerEventInterface, Project, ProjectDTO,
  ViewerEvent, GenreTypes, BrandDTO, Brand, Product,
  ProductDTO, ProjectTypes, SnapshotDataDTO, SnapshotDataEntity, Order
} from 'models'
import { observable, action, computed } from 'mobx'
import { SortTabEnums } from 'pages/viewers/ViewersPage/SortTabs'
import { DropdownSelectOptionType } from 'components'
import { Filters, FilterEnums, ViewerPageTabTypes, ViewerPageColumnKeys, ViewerPageColumnKeyTypes } from './types'
import { TotalWatchColEnums, TotalWatchLineInfo } from 'pages/viewers/ViewerInfoPage/total-watch-columns'
import moment from 'moment'
import { TotalInteractionLineInfo, TotalInteractionColEnums } from 'pages/viewers/ViewerInfoPage/total-interaction-columns'
import { ViewerPageTableLine } from 'components/viewers/ViewerTabTable/types'
import { parseToLabel, reducer } from './utils'
import { TotalAddRevenueLineInfo, TotalAddRevenueColEnums } from 'pages/viewers/ViewerInfoPage/total-add-revenue-columns'
import { TotalSpendColEnums, TotalSpendLineInfo } from 'pages/viewers/ViewerInfoPage/total-spend-columns'
import { DropResult } from 'react-beautiful-dnd'
import { colors } from 'assets'
import { TotalViewerChartDTO, ViewerChartPeriod } from '../../components/charts/DualAxis/types'
import { VIEWERS_TABLE_STORE } from 'store/ViewersTableStore'
import { FilterPeriods } from 'store/ActionHistoryTableStore'
import { OnSelectCallbackParam } from 'react-daterange-picker'

export const VIEWERS_STORE = 'VIEWERS_STORE'

export class ViewersStore {
  @observable viewers: Viewer[] = []
  @observable currentViewer: Viewer = null
  @observable projects: Map<number, Project> = new Map()
  @observable brands: Map<number, Brand> = new Map()
  @observable products: Map<number, Product> = new Map()
  @observable selectedSortTab: SortTabEnums = SortTabEnums.TOTAL_WATCH
  @observable viewerPageSelectedTab: ViewerPageTabTypes = ViewerPageTabTypes.TOTAL_WATCH_VIDEO
  @observable filters: Filters = {} as Filters
  @observable viewerPageColumnKeys: ViewerPageColumnKeys = {
    [ViewerPageTabTypes.TOTAL_WATCH_VIDEO]: Object.values(TotalWatchColEnums),
    [ViewerPageTabTypes.TOTAL_INTERACTION_TIME]: Object.values(TotalInteractionColEnums),
    [ViewerPageTabTypes.ADD_REVENUE]: Object.values(TotalAddRevenueColEnums),
    [ViewerPageTabTypes.TOTAL_SPEND]: Object.values(TotalSpendColEnums)
  }

  @observable viewerTotalStats: Map<String, ViewerChartPeriod> = new Map()
  @observable uniqueViewers = 0
  @observable uniqueProjects = 0
  @observable addExpenses = 0
  @observable revenue = 0
  @observable totalWatchTime = 0
  @observable totalInteractionTime = 0

  @observable public filterPeriod: FilterPeriods = FilterPeriods.PAST_WEEK
  @observable public searchPeriod: OnSelectCallbackParam = null

  rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @action.bound setSelectedSortTab (tab: SortTabEnums) {
    this.selectedSortTab = tab
  }

  @action.bound setViewers (data) {
    const [
      viewers, stats, orderStats
    ] = data as [ViewerInterface[], ViewerStatistics[], ViewerOrderStatistics[]]

    const statsCatalog = parseToCatalog<ViewerStatistics>(stats, {}, 'userId')
    const orderStatsCatalog = parseToCatalog<ViewerOrderStatistics>(orderStats, {}, 'userId')

    this.viewers = viewers.map(viewer => new Viewer({
      ...viewer,
      totalWatchTime: (statsCatalog[viewer.emailAddress] || {}).totalWatchTime || 0,
      totalInteractionTime: (statsCatalog[viewer.emailAddress] || {})
        .totalInteractionTime || 0,
      totalAddRevenue: (statsCatalog[viewer.emailAddress] || {}).totalAddRevenue || 0,
      totalSpent: (orderStatsCatalog[viewer.id] || {}).totalSpent || 0
    }))
  }

  @action.bound setProjects (projects: ProjectDTO[]) {
    this.projects = new Map(
      projects.map(p => [p.id, new Project(p)])
    )
    // projects.forEach(p => this.projects.set(p.id, new Project(p)))
  }

  @action.bound setBrands (brands: BrandDTO[]) {
    brands.forEach(b => this.brands.set(b.id, new Brand(b)))
  }

  @action.bound setProducts (products: ProductDTO[]) {
    products.forEach(p => this.products.set(p.id, new Product(p)))
  }

  @action.bound setFilters (filterType: FilterEnums, selection: DropdownSelectOptionType[]) {
    this.filters[filterType] = selection
  }

  @action.bound clearAllFilters () {
    this.filters = {} as Filters
  }

  @action.bound setCurrentViewer (viewer: Viewer) {
    this.currentViewer = viewer
  }

  @action.bound selectViewerPageTab (tab: ViewerPageTabTypes) {
    this.viewerPageSelectedTab = tab
    this.clearObservablesOnViewerPageTabChange()
  }

  @action.bound clearObservablesOnViewerPageTabChange () {
    this.filters = {}
    // this.projects = new Map()
    // this.brands = new Map()
    // this.products = new Map()
  }

  @action.bound setViewerPageColKeysForCurrentTab (colKeys: ViewerPageColumnKeyTypes) {
    this.viewerPageColumnKeys[this.viewerPageSelectedTab] = colKeys
  }

  @action private setFilterPeriod = (filterPeriod: FilterPeriods) => {
    this.filterPeriod = filterPeriod
  }

  @action private setSearchPeriod = (period: OnSelectCallbackParam) => {
    this.searchPeriod = period
  }

  getViewers = () => {
    const {
      [GENERAL_STORE]: { setLoadingOn, setLoadingOff, handleApiError },
      [VIEWERS_TABLE_STORE]: { triggerSearch }
    } = this.rootStore

    setLoadingOn()

    Promise.all([
      Api.get('/users/all?roleName=VIEWER', 'userApi'),
      Api.get('/stats/users', 'statsApi'),
      Api.get('/order/statistic/users')
    ])
      .then(data => {
        this.setViewers(data)
        triggerSearch()
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  getViewerById = (id: string | number) => {
    if (typeof id === 'string') {
      try {
        id = Number(id)
      } catch {
        return null
      }
    }

    return this.viewers.find(v => v.id === id)
  }

  getProjectById = (id: string | number) => {
    if (typeof id === 'string') {
      try {
        id = Number(id)
      } catch {
        return null
      }
    }

    return this.projects.get(id)
  }

  getBrandById = (id: string | number) => {
    if (typeof id === 'string') {
      try {
        id = Number(id)
      } catch {
        return null
      }
    }

    return this.brands.get(id)
  }

  getProductById = (id: string | number) => {
    if (typeof id === 'string') {
      try {
        id = Number(id)
      } catch {
        return null
      }
    }

    return this.products.get(id)
  }

  loadViewerDetails = (id: string | number) => {
    const {
      setLoadingOn, setLoadingOff, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()
    let viewer: Viewer = null
    Api
      .get(`/users/${id}`, 'userApi')
      .then((details: ViewerInterface) => {
        viewer = new Viewer(details)
        return Promise.all([
          Api.get(`/stats/users/${encodeURIComponent(details.emailAddress)}`, 'statsApi'),
          Api.get('/order/statistic/users')
        ])
      })
      .then((data: [ViewerStatistics, ViewerOrderStatistics[]]) => {
        const [stats, orderStats] = data

        const totalSpent = (orderStats.find(stat => stat.userId === viewer.id) ||
          {}).totalSpent || 0
        stats.totalSpent = totalSpent
        viewer.setStatistics(stats)
        this.setCurrentViewer(viewer)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  loadDataForViewerPageTab = () => {
    if (!this.currentViewer) return

    const loadViewerEventsPromise = (eventType: ViewerEventEnums): Promise<any> => {
      return this.currentViewer.areEventsLoaded(eventType)
        ? Promise.resolve(undefined)
        : Api.post(
          `/stats/users/${encodeURIComponent(this.currentViewer.emailAddress)}/events/`,
          { events: [eventType] },
          'statsApi'
        )
    }

    const loadViewerOrdersPromise = (): Promise<any> => {
      return this.currentViewer.areOrdersLoaded()
        ? Promise.resolve(undefined)
        : Api.get(`/order/products/users/${this.currentViewer.id}`)
    }

    const loadViewerSnapshotDataPromise = (): Promise<any> => {
      return this.currentViewer.isSnapshotDataLoaded()
        ? Promise.resolve(undefined)
        : Api.get(`/stats/users/${encodeURIComponent(this.currentViewer.emailAddress)}/snapshots/`, 'statsApi')
    }

    const loadProjectsPromise = (): Promise<any> => {
      return this.projects.size === 0
        ? Api.get('/project/all')
        : Promise.resolve(undefined)
    }

    const loadBrandsPromise = (): Promise<any> => {
      return this.brands.size === 0
        ? Api.get('/brand/all')
        : Promise.resolve(undefined)
    }

    const loadProductsPromise = (): Promise<any> => {
      return this.products.size === 0
        ? Api.get('/product/all')
        : Promise.resolve(undefined)
    }

    const promises = []
    let handleResponse

    switch (this.viewerPageSelectedTab) {
      case ViewerPageTabTypes.TOTAL_WATCH_VIDEO: {
        const eventType = ViewerEventEnums.VIDEO_STOPPED
        promises.push(loadProjectsPromise())
        promises.push(loadViewerEventsPromise(eventType))
        handleResponse = (data: [ProjectDTO[], ViewerEventInterface[]]) => {
          const [projects, events] = data
          projects && this.setProjects(projects)
          events && this.currentViewer.serViewerEvents(events, eventType)
        }
        break
      }
      case ViewerPageTabTypes.TOTAL_INTERACTION_TIME: {
        const eventType = ViewerEventEnums.INTERACTION_STOPPED
        promises.push(loadProjectsPromise())
        promises.push(loadBrandsPromise())
        promises.push(loadProductsPromise())
        promises.push(loadViewerEventsPromise(eventType))

        handleResponse = (
          data: [
            ProjectDTO[],
            BrandDTO[],
            ProductDTO[],
            ViewerEventInterface[]
          ]
        ) => {
          const [projects, brands, products, allEvents] = data
          const events = allEvents ? allEvents.filter(e => e.brandId) : undefined
          projects && this.setProjects(projects)
          brands && this.setBrands(brands)
          products && this.setProducts(products)
          events && this.currentViewer.serViewerEvents(events, eventType)
        }
        break
      }
      case ViewerPageTabTypes.ADD_REVENUE: {
        promises.push(loadViewerSnapshotDataPromise())
        promises.push(loadBrandsPromise())
        promises.push(loadProductsPromise())
        promises.push(loadProjectsPromise())
        handleResponse = (data: [
          SnapshotDataDTO[],
          BrandDTO[],
          ProductDTO[],
          ProjectDTO[]
        ]) => {
          const [snapshotData, brands, products, projects] = data
          projects && this.setProjects(projects)
          brands && this.setBrands(brands)
          products && this.setProducts(products)
          snapshotData && this.currentViewer.setSnapshotData(snapshotData)
        }
        break
      }
      case ViewerPageTabTypes.TOTAL_SPEND: {
        promises.push(loadViewerOrdersPromise())
        promises.push(loadBrandsPromise())
        promises.push(loadProductsPromise())
        promises.push(loadProjectsPromise())
        handleResponse = (data: [
          OrderInterface[],
          BrandDTO[],
          ProductDTO[],
          ProjectDTO[]
        ]) => {
          const [orders, brands, products, projects] = data
          projects && this.setProjects(projects)
          brands && this.setBrands(brands)
          products && this.setProducts(products)
          orders && this.currentViewer.setOrders(orders)
        }
        break
      }
      default: break
    }

    const {
      setLoadingOn, setLoadingOff, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()
    Promise.all(promises)
      .then(data => {
        handleResponse(data)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  getViewersStats = () => {
    const {
      handleApiError
    } = this.rootStore[GENERAL_STORE]

    const {
      startDate,
      endDate
    } = getStartAndEndDates(this.searchPeriod, this.filterPeriod)

    Api.post('/stats/summary/totalPeriod', {
      startDate,
      endDate
    }, 'statsApi')
      .then((data: TotalViewerChartDTO) => {
        Api.post('/order/statistic/period', {
          startDate: moment(startDate != null ? startDate : moment()).format('YYYY-MM-DD'),
          endDate: moment(endDate != null ? endDate : moment()).format('YYYY-MM-DD')
        })
          .then((orders: Map<String, number>) => {
            const viewerData: Map<String, ViewerChartPeriod> = new Map()
            console.log('orders', orders)
            Object.keys(data.periodStats).forEach((value) => {
              console.log('orders[value]', orders[value])
              viewerData.set(value, data.periodStats[value])
              viewerData.get(value).totalOrders = orders[value]
              this.totalWatchTime += data.periodStats[value].totalWatchTime
              this.addExpenses += data.periodStats[value].revenue
              this.revenue = orders[value]?.total ? this.revenue + orders[value].total : this.revenue
              this.totalInteractionTime += data.periodStats[value].totalInteraction
            })
            this.viewerTotalStats = viewerData
            this.uniqueProjects = data.uniqueProjects
            this.uniqueViewers = data.uniqueViewers
          }).catch(handleApiError)
      }).catch(handleApiError)
  }

  filterByProject = (entity: ViewerEvent | SnapshotDataEntity | Order) => {
    const projectFilters = this.filters[FilterEnums.PROJECT] || []
    if (!projectFilters.length) return true
    return projectFilters.some(f => f.value === entity.projectId)
  }

  filterByBrand = (entity: ViewerEvent | SnapshotDataEntity | Order) => {
    const brandFilters = this.filters[FilterEnums.BRAND] || []
    if (!brandFilters.length) return true
    return brandFilters.some(f => f.value === entity.brandId)
  }

  handleColumnLabelDragEnd = (result: DropResult): void => {
    try {
      const {
        draggableId: colKey,
        source: { index: sourceIndex },
        destination: { index: destinationIndex }
      } = result

      const cloneColKeys = [...this.viewerPageColKeysForCurrentTab] as ViewerPageColumnKeyTypes
      if (destinationIndex === cloneColKeys.length - 1) return

      const increment = destinationIndex < sourceIndex ? -1 : 1

      for (let k = sourceIndex; k !== destinationIndex; k += increment) {
        cloneColKeys[k] = cloneColKeys[k + increment]
      }
      cloneColKeys[destinationIndex] = colKey as any
      this.setViewerPageColKeysForCurrentTab(cloneColKeys)
    } catch (err) {
      console.error(err)
    }
  }

  public onFilterPeriodChange = (filterPeriod: FilterPeriods) => {
    this.setFilterPeriod(filterPeriod)
    if (filterPeriod === FilterPeriods.CUSTOM) return
    this.setSearchPeriod(null)
    this.getViewersStats()
  }

  public onSearchPeriodChange = (period: OnSelectCallbackParam) => {
    this.onFilterPeriodChange(FilterPeriods.CUSTOM)
    this.setSearchPeriod(period)
    this.getViewersStats()
  }

  @computed get sortedViewers () {
    let sortBy
    switch (this.selectedSortTab) {
      case SortTabEnums.TOTAL_WATCH: {
        sortBy = (a, b) => b.totalWatchTime - a.totalWatchTime
        break
      }
      case SortTabEnums.INTERACTION_TIME: {
        sortBy = (a, b) => b.totalInteractionTime - a.totalInteractionTime
        break
      }
      case SortTabEnums.ADD_REVENUE: {
        sortBy = (a, b) => b.totalAddRevenue - a.totalAddRevenue
        break
      }
      case SortTabEnums.TOTAL_SPENT: {
        sortBy = (a, b) => b.totalSpent - a.totalSpent
        break
      }
      default: return this.viewers
    }
    return this.viewers.slice().sort(sortBy)
  }

  @computed get currentViewerEvents (): ViewerEvent[] | SnapshotDataEntity[] | Order[] {
    if (!this.currentViewer) return []

    if (this.viewerPageSelectedTab === ViewerPageTabTypes.ADD_REVENUE) {
      return this.currentViewer.snapshotData || []
    }

    if (this.viewerPageSelectedTab === ViewerPageTabTypes.TOTAL_SPEND) {
      return this.currentViewer.orders || []
    }

    if (!this.currentViewer.events) return []

    const eventType = {
      [ViewerPageTabTypes.TOTAL_WATCH_VIDEO]: ViewerEventEnums.VIDEO_STOPPED,
      [ViewerPageTabTypes.TOTAL_INTERACTION_TIME]: ViewerEventEnums.INTERACTION_STOPPED
    }[this.viewerPageSelectedTab]

    return this.currentViewer.events[eventType] || []
  }

  @computed get totalWatchVideoEventProjectMap () {
    const eventProjectMap = new Map<ViewerEvent, Project>(
      (this.currentViewerEvents as ViewerEvent[])
        .map(e => [e, this.getProjectById(e.projectId)])
    )

    return [...eventProjectMap]
      .filter(([e]) => this.filterByProject(e))
      .filter(([_, p]) => {
        const filters = this.filters[FilterEnums.PROJECT_TYPE] || []
        if (!filters.length) return true
        return filters.some(f => f.value === p.type)
      })
      .filter(([_, p]) => {
        const filters = this.filters[FilterEnums.GENRE] || []
        if (!filters.length) return true
        return filters.some(f => p.genres.includes(f.value as GenreTypes))
      })
  }

  @computed get totalInteractionMap () {
    const brandIdEventsMap = new Map<number, ViewerEvent[]>()
    const events = this.currentViewerEvents as ViewerEvent[]
    events
      .filter(this.filterByProject)
      .filter(this.filterByBrand)
      .forEach(e => {
        if (!e.brandId) return
        let eventsForThisBrand: ViewerEvent[] = []
        if (brandIdEventsMap.has(e.brandId)) {
          eventsForThisBrand = brandIdEventsMap.get(e.brandId)
        }
        eventsForThisBrand.push(e)
        brandIdEventsMap.set(e.brandId, eventsForThisBrand)
      })

    const brandIdProjectIdEventMap = new Map<number, Map<number, ViewerEvent[]>>(
      [...brandIdEventsMap].map(([brandId, events]) => {
        const projectIdEventsMap = new Map<number, ViewerEvent[]>()
        events.forEach(e => {
          let eventsByProject: ViewerEvent[] = []
          if (projectIdEventsMap.has(e.projectId)) {
            eventsByProject = projectIdEventsMap.get(e.projectId)
          }
          eventsByProject.push(e)
          projectIdEventsMap.set(e.projectId, eventsByProject)
        })

        return [brandId, projectIdEventsMap]
      })
    )

    return [...brandIdProjectIdEventMap]
  }

  @computed get addRevenueSnapshotData (): SnapshotDataEntity[] {
    const events = this.currentViewerEvents as SnapshotDataEntity[]
    return events
      .filter(e => {
        const eventFilters = this.filters[FilterEnums.EVENT] || []
        if (!eventFilters.length) return true
        return eventFilters.some(f => f.value === e.eventType)
      })
      .filter(this.filterByBrand)
      .filter(this.filterByProject)
  }

  @computed get totalSpendMap (): [number, Order[]][] {
    const brandIdOrdersMap = new Map<number, Order[]>()
    const orders = this.currentViewer.orders

    orders
      .filter(this.filterByBrand)
      .filter(this.filterByProject)
      .forEach(order => {
        if (!order.brandId) return
        let ordersForThisBrand: Order[] = []
        if (brandIdOrdersMap.has(order.brandId)) {
          ordersForThisBrand = brandIdOrdersMap.get(order.brandId)
        }
        ordersForThisBrand.push(order)
        brandIdOrdersMap.set(order.brandId, ordersForThisBrand)
      })
    return [...brandIdOrdersMap]
  }

  @computed get viewerPageTableRows (): ViewerPageTableLine[] {
    const totalWatchVideoRows = (): TotalWatchLineInfo[] => {
      return this.totalWatchVideoEventProjectMap
        .map(([e, p]) => ({
          [TotalWatchColEnums.PROJECT]: { text: e.projectName },
          [TotalWatchColEnums.WATCH_TIME]: {
            text: e.durationAsString, sortableValue: e.duration
          },
          [TotalWatchColEnums.PROJECT_TYPE]: { text: p?.type },
          [TotalWatchColEnums.GENRE]: { text: p?.genresAsString },
          [TotalWatchColEnums.ADDED_AT]: { text: p?.addedAtAsString },
          [TotalWatchColEnums.TAGS]: { tags: p?.tagsAsArray }
        }))
    }
    const totalInteractionTimeRows = (): ViewerPageTableLine[] => {
      // function customTemplate () {
      //   return this.duration.asSeconds() < 60
      //     ? 's [sec]'
      //     : this.duration.asMinutes() < 60
      //       ? 'm [min]'
      //       : 'H [h] m [min]'
      // }

      const result: ViewerPageTableLine[] = []

      this.totalInteractionMap.forEach(([brandId, projectIdEventMap]) => {
        const brand = this.getBrandById(brandId)
        Array.from(projectIdEventMap).forEach(([projectId, events]) => {
          const project = this.getProjectById(projectId)
          const duration = events.map(e => e.duration).reduce((sum, dur) => sum + dur, 0)
          const line: TotalInteractionLineInfo = {
            [TotalInteractionColEnums.BRAND]: {
              image: brand.imageUrl,
              text: brand.name,
              expandable: true
            },
            [TotalInteractionColEnums.PROJECT]: { text: project?.name },
            [TotalInteractionColEnums.INTERACT]: {
              text: formatDuration(duration), sortableValue: duration
            },
            [TotalInteractionColEnums.ADDED_AT]: { text: moment(brand.createdDate).format('HH:mm, DD MMM YYYY') },
            [TotalInteractionColEnums.TAGS]: { tags: brand.tagsAsArray },
            children: events.filter(e => e.productId).map(e => {
              const product = this.getProductById(e?.productId)
              return {
                [TotalInteractionColEnums.BRAND]: {
                  image: product?.imageUrl,
                  text: product?.name
                },
                [TotalInteractionColEnums.PROJECT]: { text: project?.name },
                [TotalInteractionColEnums.INTERACT]: {
                  text: e.durationAsString, sortableValue: e.duration
                },
                [TotalInteractionColEnums.ADDED_AT]: { text: moment(product?.createdDate).format('HH:mm, DD MMM YYYY') },
                [TotalInteractionColEnums.TAGS]: { tags: product?.tagsAsArray }
              }
            })
          }
          result.push(line as ViewerPageTableLine)
        })
      })

      return result
    }
    const addRevenueRows = (): ViewerPageTableLine[] => {
      const [firstColumnLabel] = this.viewerPageColKeysForCurrentTab

      switch (firstColumnLabel) {
        case TotalAddRevenueColEnums.EVENT: {
          type SnapshotDataCatalog = { [key in ViewerEventEnums]?: SnapshotDataEntity[] }
          const eventsCatalog: SnapshotDataCatalog = {} as SnapshotDataCatalog
          this.addRevenueSnapshotData.forEach(event => {
            eventsCatalog[event.eventType] = eventsCatalog[event.eventType] || []
            eventsCatalog[event.eventType].push(event)
          })

          return Object.keys(eventsCatalog).map((key: ViewerEventEnums) => {
            const brandIds = new Set<number>()
            const projectIds = new Set<number>()
            const snapshotEntities = eventsCatalog[key]

            const totalRevenueForThisEventType = snapshotEntities
              .map(snapshotEntity => {
                brandIds.add(snapshotEntity.brandId)
                projectIds.add(snapshotEntity.projectId)
                return snapshotEntity.revenue || 0
              })
              .reduce(reducer, 0)
            const eventTypeAsLabel = parseToLabel(key)

            type EntitiesByBrandAndByProject =
              { [key: number]: { [key: number]: SnapshotDataEntity[] } }
            const entitiesByBrandIdAndByProjectId: EntitiesByBrandAndByProject =
              {} as EntitiesByBrandAndByProject
            snapshotEntities.forEach(entity => {
              const { brandId, projectId } = entity
              entitiesByBrandIdAndByProjectId[brandId] =
                entitiesByBrandIdAndByProjectId[brandId] || {}
              entitiesByBrandIdAndByProjectId[brandId][projectId] =
                entitiesByBrandIdAndByProjectId[brandId][projectId] || []
              entitiesByBrandIdAndByProjectId[brandId][projectId].push(entity)
            })

            const children: TotalAddRevenueLineInfo[] = []

            Object.keys(entitiesByBrandIdAndByProjectId)
              .forEach(brandId => {
                const brand = this.getBrandById(brandId)
                const entitiesByProjectId = entitiesByBrandIdAndByProjectId[brandId]
                Object.keys(entitiesByProjectId)
                  .forEach(projectId => {
                    const project = this.getProjectById(projectId)
                    const entitiesForProjectBrandPair =
                      entitiesByProjectId[projectId] as SnapshotDataEntity[]
                    const totalChildRevenue = entitiesForProjectBrandPair
                      .map(entity => entity.revenue)
                      .reduce(reducer, 0)

                    const praChildren: TotalAddRevenueLineInfo[] = entitiesForProjectBrandPair
                      .filter(entity => entity.productId)
                      .map(entity => {
                        const product = this.getProductById(entity.productId)
                        return {
                          [TotalAddRevenueColEnums.EVENT]: { text: eventTypeAsLabel },
                          [TotalAddRevenueColEnums.BRAND]: {
                            text: product.name, image: product.imageUrl
                          },
                          [TotalAddRevenueColEnums.PROJECT]: { text: project.name },
                          [TotalAddRevenueColEnums.REVENUE]: {
                            moneyAmount: entity.revenue,
                            sortableValue: entity.revenue
                          }
                        }
                      })

                    children.push({
                      [TotalAddRevenueColEnums.EVENT]: {
                        text: eventTypeAsLabel, expandable: true
                      },
                      [TotalAddRevenueColEnums.BRAND]: {
                        text: brand.name, image: brand.imageUrl
                      },
                      [TotalAddRevenueColEnums.PROJECT]: { text: project.name },
                      [TotalAddRevenueColEnums.REVENUE]: {
                        moneyAmount: totalChildRevenue,
                        sortableValue: totalChildRevenue
                      },
                      children: praChildren
                    })
                  })
              })

            const line: TotalAddRevenueLineInfo = children.length === 1
              ? children[0]
              : {
                [TotalAddRevenueColEnums.EVENT]: { text: eventTypeAsLabel, expandable: true },
                [TotalAddRevenueColEnums.BRAND]: {
                  aggregationValue: brandIds.size, aggregationEntity: 'brand'
                },
                [TotalAddRevenueColEnums.PROJECT]: {
                  aggregationValue: projectIds.size, aggregationEntity: 'project'
                },
                [TotalAddRevenueColEnums.REVENUE]: {
                  moneyAmount: totalRevenueForThisEventType,
                  sortableValue: totalRevenueForThisEventType
                },
                children
              }
            line[TotalAddRevenueColEnums.REVENUE].style = { color: colors.blue, fontWeight: 'bold', justifyContent: 'flex-end' }
            return line as ViewerPageTableLine
          })
        }

        case TotalAddRevenueColEnums.BRAND: {
          type SnapshotDataCatalogByBrandId = {
            [key: string]: SnapshotDataEntity[]
          }

          const eventsCatalog: SnapshotDataCatalogByBrandId =
            {} as SnapshotDataCatalogByBrandId

          this.addRevenueSnapshotData
            .forEach(event => {
              if (!eventsCatalog[event.brandId]) {
                eventsCatalog[event.brandId] = []
              }
              eventsCatalog[event.brandId].push(event)
            })

          return Object.keys(eventsCatalog)
            .map(key => {
              const brand = this.getBrandById(key)
              const projectIds = new Set<number>()
              const eventTypes = new Set<ViewerEventEnums>()
              const snapshotEntities = eventsCatalog[key]

              const totalRevenueForThisBrand = snapshotEntities
                .map(snapshotEntity => {
                  projectIds.add(snapshotEntity.projectId)
                  eventTypes.add(snapshotEntity.eventType)
                  return snapshotEntity.revenue || 0
                })
                .reduce(reducer, 0)

              type EntitiesByEventTypeAndByProject =
                { [key in ViewerEventEnums]: { [key: number]: SnapshotDataEntity[] } }
              const entitiesByEventTypeAndByProjectId: EntitiesByEventTypeAndByProject =
                {} as EntitiesByEventTypeAndByProject

              snapshotEntities.forEach(entity => {
                const { eventType, projectId } = entity
                if (!entitiesByEventTypeAndByProjectId[eventType]) {
                  entitiesByEventTypeAndByProjectId[eventType] = {}
                }
                if (!entitiesByEventTypeAndByProjectId[eventType][projectId]) {
                  entitiesByEventTypeAndByProjectId[eventType][projectId] = []
                }
                entitiesByEventTypeAndByProjectId[eventType][projectId].push(entity)
              })

              const children: TotalAddRevenueLineInfo[] = []

              Object.keys(entitiesByEventTypeAndByProjectId)
                .forEach(eventType => {
                  const eventTypeAsLabel = parseToLabel(eventType)
                  const entitiesByProjectId = entitiesByEventTypeAndByProjectId[eventType]

                  Object.keys(entitiesByProjectId)
                    .forEach(projectId => {
                      const project = this.getProjectById(projectId)
                      const entitiesForProjectBrandPair =
                        entitiesByProjectId[projectId] as SnapshotDataEntity[]
                      const totalChildRevenue = entitiesForProjectBrandPair
                        .map(entity => entity.revenue)
                        .reduce(reducer, 0)

                      const praChildren: TotalAddRevenueLineInfo[] =
                        entitiesForProjectBrandPair
                          .filter(entity => entity.productId)
                          .map(entity => {
                            const product = this.getProductById(entity.productId)
                            return {
                              [TotalAddRevenueColEnums.BRAND]: {
                                text: product.name, image: product.imageUrl, expandable: true
                              },
                              [TotalAddRevenueColEnums.EVENT]: { text: eventTypeAsLabel },
                              [TotalAddRevenueColEnums.PROJECT]: { text: project.name },
                              [TotalAddRevenueColEnums.REVENUE]: {
                                moneyAmount: entity.revenue, sortableValue: entity.revenue
                              }
                            }
                          })

                      children.push({
                        [TotalAddRevenueColEnums.BRAND]: {
                          text: brand.name, image: brand.imageUrl, expandable: true
                        },
                        [TotalAddRevenueColEnums.EVENT]: { text: eventTypeAsLabel },
                        [TotalAddRevenueColEnums.PROJECT]: { text: project.name },
                        [TotalAddRevenueColEnums.REVENUE]: {
                          moneyAmount: totalChildRevenue, sortableValue: totalChildRevenue
                        },
                        children: praChildren
                      })
                    })
                })

              const line: TotalAddRevenueLineInfo = children.length === 1
                ? children[0]
                : {
                  [TotalAddRevenueColEnums.BRAND]: {
                    text: `${brand.name}`, image: brand.imageUrl, expandable: true
                  },
                  [TotalAddRevenueColEnums.EVENT]: {
                    aggregationValue: eventTypes.size, aggregationEntity: 'event'
                  },
                  [TotalAddRevenueColEnums.PROJECT]: {
                    aggregationValue: projectIds.size, aggregationEntity: 'project'
                  },
                  [TotalAddRevenueColEnums.REVENUE]: {
                    moneyAmount: totalRevenueForThisBrand,
                    sortableValue: totalRevenueForThisBrand
                  },
                  children
                }
              return line as ViewerPageTableLine
            })
        }

        case TotalAddRevenueColEnums.PROJECT: {
          type SnapshotDataCatalogByProjectId = {
            [key: string]: SnapshotDataEntity[]
          }

          const eventsCatalog: SnapshotDataCatalogByProjectId =
            {} as SnapshotDataCatalogByProjectId

          this.addRevenueSnapshotData
            .forEach(event => {
              if (!eventsCatalog[event.projectId]) {
                eventsCatalog[event.projectId] = []
              }
              eventsCatalog[event.projectId].push(event)
            })

          return Object.keys(eventsCatalog)
            .map(key => {
              const project = this.getProjectById(key)
              const brandIds = new Set<number>()
              const eventTypes = new Set<ViewerEventEnums>()
              const snapshotEntities = eventsCatalog[key]

              const totalRevenueForThisProject = snapshotEntities
                .map(entity => {
                  brandIds.add(entity.brandId)
                  eventTypes.add(entity.eventType)
                  return entity.revenue
                })
                .reduce(reducer, 0)

              type EntitiesByBrandIdAndByEventType = {
                [key: number]: {
                  [key in ViewerEventEnums]?: SnapshotDataEntity[]
                }
              }
              const entitiesByBrandIdAndByEventType: EntitiesByBrandIdAndByEventType =
                {} as EntitiesByBrandIdAndByEventType

              snapshotEntities.forEach(entity => {
                const { brandId, eventType } = entity
                if (!entitiesByBrandIdAndByEventType[brandId]) {
                  entitiesByBrandIdAndByEventType[brandId] = {}
                }
                if (!entitiesByBrandIdAndByEventType[brandId][eventType]) {
                  entitiesByBrandIdAndByEventType[brandId][eventType] = []
                }
                entitiesByBrandIdAndByEventType[brandId][eventType].push(entity)
              })

              const children: TotalAddRevenueLineInfo[] = []

              Object.keys(entitiesByBrandIdAndByEventType)
                .forEach(brandId => {
                  const brand = this.getBrandById(brandId)
                  const entitiesByEventType = entitiesByBrandIdAndByEventType[brandId]

                  Object.keys(entitiesByEventType)
                    .forEach(eventType => {
                      const eventTypeAsLabel = parseToLabel(eventType)
                      const entitiesForProjectBradPair =
                        entitiesByEventType[eventType] as SnapshotDataEntity[]
                      const totalChildRevenue = entitiesForProjectBradPair
                        .map(entity => entity.revenue)
                        .reduce(reducer, 0)

                      const praChildren: TotalAddRevenueLineInfo[] =
                        entitiesForProjectBradPair
                          .filter(entity => entity.productId)
                          .map(entity => {
                            const product = this.getProductById(entity.productId)
                            return ({
                              [TotalAddRevenueColEnums.PROJECT]: { text: project.name },
                              [TotalAddRevenueColEnums.BRAND]: {
                                text: product.name, image: product.imageUrl
                              },
                              [TotalAddRevenueColEnums.EVENT]: { text: eventTypeAsLabel },
                              [TotalAddRevenueColEnums.REVENUE]: {
                                moneyAmount: entity.revenue,
                                sortableValue: entity.revenue
                              }
                            })
                          })

                      children.push({
                        [TotalAddRevenueColEnums.PROJECT]: {
                          text: project.name, expandable: true
                        },
                        [TotalAddRevenueColEnums.BRAND]: {
                          text: brand.name, image: brand.imageUrl
                        },
                        [TotalAddRevenueColEnums.EVENT]: {
                          text: eventTypeAsLabel
                        },
                        [TotalAddRevenueColEnums.REVENUE]: {
                          moneyAmount: totalChildRevenue,
                          sortableValue: totalChildRevenue
                        },
                        children: praChildren
                      })
                    })
                })

              const line: TotalAddRevenueLineInfo = children.length === 1
                ? children[0]
                : {
                  [TotalAddRevenueColEnums.PROJECT]: { text: project.name, expandable: true },
                  [TotalAddRevenueColEnums.BRAND]: {
                    aggregationValue: brandIds.size, aggregationEntity: 'brand'
                  },
                  [TotalAddRevenueColEnums.EVENT]: {
                    aggregationValue: eventTypes.size, aggregationEntity: 'event'
                  },
                  [TotalAddRevenueColEnums.REVENUE]: {
                    moneyAmount: totalRevenueForThisProject,
                    sortableValue: totalRevenueForThisProject
                  },
                  children
                }
              return line as ViewerPageTableLine
            })
        }

        default: return []
      }
    }
    const totalSpendRows = (): ViewerPageTableLine[] => {
      return this.totalSpendMap
        .map(([brandId, orders]) => {
          const brand = this.getBrandById(brandId)
          const projectIds = new Set<number>()
          let quantitySum = 0
          let totalPriceForBrand = 0
          let addedAtDateForBrand = new Date().getTime()

          const children: TotalSpendLineInfo[] =
            orders.map(o => {
              o.projectId && projectIds.add(o.projectId)
              o.count && (quantitySum += o.count)
              o.count && o.price && (totalPriceForBrand += o.totalPrice)
              addedAtDateForBrand = Math.min(o.orderDate.getTime(), addedAtDateForBrand)

              const product = this.getProductById(o.productId)
              const project = this.getProjectById(o.projectId)

              return {
                [TotalSpendColEnums.BRAND]: {
                  image: product.imageUrl,
                  text: product.name,
                  description: `SKU: ${o.productSku.sku}`
                },
                [TotalSpendColEnums.PROJECT]: {
                  text: project.name
                },
                [TotalSpendColEnums.QUANTITY]: {
                  text: o.count
                },
                [TotalSpendColEnums.ADDED_AT]: {
                  text: o.dateAsString
                },
                [TotalSpendColEnums.PRICE]: {
                  moneyAmount: o.totalPrice,
                  sortableValue: o.totalPrice
                }
              }
            })

          const line: TotalSpendLineInfo = {
            [TotalSpendColEnums.BRAND]: {
              text: brand.name, image: brand.imageUrl, expandable: true
            },
            [TotalSpendColEnums.PROJECT]: {
              aggregationValue: projectIds.size, aggregationEntity: 'project'
            },
            [TotalSpendColEnums.QUANTITY]: {
              text: quantitySum
            },
            [TotalSpendColEnums.ADDED_AT]: {
              text: moment(addedAtDateForBrand).format('HH:mm, DD MMM YYYY')
            },
            [TotalSpendColEnums.PRICE]: {
              moneyAmount: totalPriceForBrand,
              sortableValue: totalPriceForBrand
            },
            children
          }
          return line as ViewerPageTableLine
        })
    }

    switch (this.viewerPageSelectedTab) {
      case ViewerPageTabTypes.TOTAL_WATCH_VIDEO: {
        return totalWatchVideoRows()
      }
      case ViewerPageTabTypes.TOTAL_INTERACTION_TIME: {
        return totalInteractionTimeRows()
      }
      case ViewerPageTabTypes.ADD_REVENUE: {
        return addRevenueRows()
      }
      case ViewerPageTabTypes.TOTAL_SPEND: {
        return totalSpendRows()
      }
      default: return []
    }
  }

  @computed get projectsFromCurrentViewerEvents () {
    const projects = new Set<Project>()
    this.currentViewerEvents
      .forEach(e => this.getProjectById(e.projectId) && projects.add(this.getProjectById(e.projectId)))
    return Array.from(projects)
  }

  @computed get projectsAsDropdownOptions (): DropdownSelectOptionType[] {
    return this.projectsFromCurrentViewerEvents
      .map(p => ({ value: p?.id, label: p?.name }))
  }

  @computed get projectTypesAsDropdownOptions (): DropdownSelectOptionType[] {
    const projectTypes = new Set<ProjectTypes>()
    this.projectsFromCurrentViewerEvents.forEach(p => projectTypes.add(p?.type))
    return [...projectTypes]
      .map(type => ({ label: type, value: type } as DropdownSelectOptionType))
  }

  @computed get projectGenresAsDropdownOptions (): DropdownSelectOptionType[] {
    const projectGenres = new Set<GenreTypes>()
    this.projectsFromCurrentViewerEvents
      .forEach(p => p?.genres.forEach(g => projectGenres.add(g)))
    return [...projectGenres]
      .map(genre => ({ label: genre, value: genre } as DropdownSelectOptionType))
  }

  @computed get brandsFromCurrentViewerEvents () {
    const brands = new Set<Brand>()
    this.currentViewerEvents
      .forEach(e => e.brandId && brands.add(this.getBrandById(e.brandId)))
    return Array.from(brands)
  }

  @computed get brandsAsDropdownOptions (): DropdownSelectOptionType[] {
    return this.brandsFromCurrentViewerEvents.map(b => ({ value: b.id, label: b.name }))
  }

  @computed get viewerTriggeredEventTypes (): ViewerEventEnums[] {
    const triggeredEventTypes = new Set<ViewerEventEnums>()
    const events = this.currentViewerEvents as SnapshotDataEntity[]
    events.forEach(e => e.eventType && triggeredEventTypes.add(e.eventType))
    return Array.from(triggeredEventTypes)
  }

  @computed get eventTypesAsDropdownOptions (): DropdownSelectOptionType[] {
    return this.viewerTriggeredEventTypes.map(eventType => ({
      value: eventType,
      label: parseToLabel(eventType)
    }))
  }

  @computed get viewerPageTitleProps () {
    // const parseDuration = (duration: number) => {
    //   // function customTemplate () {
    //   //   return this.duration.asDays() < 1
    //   //     ? 'H [h] m [min]'
    //   //     : 'D [d] H [h]'
    //   // }
    //   return moment.duration(duration, 'millisecond').format(customTemplate)
    // }

    switch (this.viewerPageSelectedTab) {
      case ViewerPageTabTypes.TOTAL_WATCH_VIDEO: {
        const totalDuration = this.totalWatchVideoEventProjectMap
          .map(([e]) => e.duration)
          .reduce(reducer, 0)
        return {
          title: 'Total Watch Time',
          label: 'Total watch time',
          value: formatDuration(totalDuration)
        }
      }
      case ViewerPageTabTypes.TOTAL_INTERACTION_TIME: {
        const totalDuration = this.totalInteractionMap.map(([_, projectIdEventMap]) =>
          Array.from(projectIdEventMap)
            .map(([_, events]) => events
              .map(e => e.duration)
              .reduce(reducer, 0))
            .reduce(reducer, 0)
        )
          .reduce(reducer, 0)
        return {
          title: 'Total Interaction Time',
          label: 'Total interaction time',
          value: formatDuration(totalDuration)
        }
      }
      case ViewerPageTabTypes.ADD_REVENUE: {
        const totalRevenueForFilteredData = this.addRevenueSnapshotData
          .map(entity => entity.revenue)
          .reduce(reducer, 0)
        return {
          title: 'Total Add Revenue',
          label: 'Total',
          value: `$${totalRevenueForFilteredData.toFixed(2)}`
        }
      }
      case ViewerPageTabTypes.TOTAL_SPEND: {
        const totalSpendForFilteredData = this.totalSpendMap
          .map(([_, orders]) => orders.map(o => o.totalPrice).reduce(reducer, 0))
          .reduce(reducer, 0)
        return {
          title: 'Total Spend',
          label: 'Total Price Spend',
          value: `$${totalSpendForFilteredData.toFixed(2)}`
        }
      }
      default: return {
        title: 'Title',
        label: 'Total',
        value: '0'
      }
    }
  }

  @computed get viewerPageColKeysForCurrentTab () {
    return this.viewerPageColumnKeys[this.viewerPageSelectedTab]
  }
}
